<template>
    <div
        class="service-group-card rounded-xl border-2 transition-all duration-200 overflow-hidden"
        :class="cardClasses"
    >
        <!-- Group Header -->
        <div
            class="group-header px-4 py-3 flex items-center justify-between cursor-pointer"
            :class="headerClasses"
            @click="toggleExpanded"
        >
            <div class="flex items-center space-x-3">
                <!-- Icon -->
                <span class="text-2xl">{{ groupIcon }}</span>

                <!-- Title & Info -->
                <div>
                    <div class="flex items-center space-x-2">
                        <h3 class="font-semibold text-gray-900">{{ group.name }}</h3>
                        <span
                            v-if="isMandatory"
                            class="px-2 py-0.5 text-xs font-medium rounded-full"
                            :class="isValid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                        >
                            {{ isValid ? '✓ Erledigt' : 'Pflicht' }}
                        </span>
                    </div>
                    <p class="text-xs text-gray-500 mt-0.5">{{ requirementText }}</p>
                </div>
            </div>

            <!-- Status & Expand -->
            <div class="flex items-center space-x-2">
                <span v-if="selectedCount > 0" class="text-sm font-medium text-gray-600">
                    {{ selectedCount }} ausgewählt
                </span>
                <svg
                    class="w-5 h-5 text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': expanded }"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
            </div>
        </div>

        <!-- Group Content -->
        <div
            v-show="expanded"
            class="group-content border-t border-gray-100"
        >
            <!-- Help Text -->
            <div v-if="group.help_text" class="px-4 py-2 bg-blue-50 text-blue-700 text-sm">
                <span class="mr-1">💡</span> {{ group.help_text }}
            </div>

            <!-- Services Grid/List -->
            <div class="p-3">
                <!-- Grid layout for exclusive choice (radio buttons) -->
                <div
                    v-if="group.type === 'exclusive_choice'"
                    class="grid gap-2"
                    :class="group.services.length <= 4 ? 'grid-cols-2' : 'grid-cols-1'"
                >
                    <label
                        v-for="service in group.services"
                        :key="service.id"
                        class="service-card relative flex items-center p-3 rounded-lg border-2 transition-all duration-150"
                        :class="serviceCardClass(service.id)"
                        @click="onTileClick($event, service.id)"
                    >
                        <input
                            type="radio"
                            :name="'group_' + group.id"
                            :value="service.id"
                            :checked="isServiceSelected(service.id)"
                            :disabled="isServiceDisabled(service.id)"
                            @change="onInputChange($event, service.id)"
                            class="sr-only"
                        />
                        <div
                            class="w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 flex-shrink-0 transition-colors"
                            :class="serviceIndicatorClass(service.id)"
                        >
                            <svg
                                v-if="isServiceRemoving(service.id)"
                                class="w-3 h-3 text-white"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <div
                                v-else-if="isServiceSelected(service.id)"
                                class="w-2 h-2 rounded-full bg-white"
                            ></div>
                        </div>
                        <div class="flex-1 min-w-0">
                            <span class="font-medium text-gray-900 text-sm" :class="{ 'line-through': isServiceRemoving(service.id) }">{{ service.name }}</span>
                            <span v-if="isServiceRemoving(service.id)" class="ml-2 text-xs text-red-600">(wird entfernt)</span>
                            <span v-else-if="isServiceRemovable(service.id)" class="ml-2 text-xs text-green-600">(zugewiesen – antippen zum Entfernen)</span>
                            <span v-else-if="isServiceBooked(service.id)" class="ml-2 text-xs text-green-600">(bereits zugewiesen)</span>
                            <span v-else-if="isServiceExcluded(service.id)" class="ml-2 text-xs text-gray-400">(nicht kombinierbar)</span>
                            <p v-if="service.description" class="text-xs text-gray-500 truncate mt-0.5">
                                {{ service.description }}
                            </p>
                        </div>
                    </label>
                </div>

                <!-- List layout for checkboxes -->
                <div v-else class="space-y-2">
                    <label
                        v-for="service in group.services"
                        :key="service.id"
                        class="service-card flex items-center p-3 rounded-lg border-2 transition-all duration-150"
                        :class="serviceCardClass(service.id)"
                        @click="onTileClick($event, service.id)"
                    >
                        <input
                            type="checkbox"
                            :value="service.id"
                            :checked="isServiceSelected(service.id)"
                            :disabled="isServiceDisabled(service.id)"
                            @change="onInputChange($event, service.id)"
                            class="sr-only"
                        />
                        <div
                            class="w-5 h-5 rounded border-2 flex items-center justify-center mr-3 flex-shrink-0 transition-colors"
                            :class="serviceIndicatorClass(service.id)"
                        >
                            <svg
                                v-if="isServiceRemoving(service.id)"
                                class="w-3 h-3 text-white"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <svg
                                v-else-if="isServiceSelected(service.id)"
                                class="w-3 h-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div class="flex-1 min-w-0">
                            <span class="font-medium text-gray-900 text-sm" :class="{ 'line-through': isServiceRemoving(service.id) }">{{ service.name }}</span>
                            <span v-if="isServiceRemoving(service.id)" class="ml-2 text-xs text-red-600">(wird entfernt)</span>
                            <span v-else-if="isServiceRemovable(service.id)" class="ml-2 text-xs text-green-600">(zugewiesen – antippen zum Entfernen)</span>
                            <span v-else-if="isServiceBooked(service.id)" class="ml-2 text-xs text-green-600">(bereits zugewiesen)</span>
                            <span v-else-if="isServiceExcluded(service.id)" class="ml-2 text-xs text-gray-400">(nicht kombinierbar)</span>
                            <p v-if="service.description" class="text-xs text-gray-500 mt-0.5">
                                {{ service.description }}
                            </p>
                        </div>
                    </label>
                </div>
            </div>

            <!-- Validation Message -->
            <div
                v-if="validationMessage && hasValidationError"
                class="px-4 py-2 bg-red-50 border-t border-red-100"
            >
                <p class="text-sm text-red-600 flex items-center">
                    <svg class="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {{ validationMessage }}
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, PropType } from 'vue'
import { getIconForGroup, isMandatoryGroup, requiredMinimum, validateGroupLocally } from '../composables/useServiceGroups'

interface Service {
    id: number
    name: string
    description?: string
    is_default?: boolean
}

interface ServiceGroup {
    id: number
    name: string
    description?: string
    type: 'optional' | 'mandatory_choice' | 'exclusive_choice' | 'multi_required'
    min_selections?: number
    help_text?: string
    services: Service[]
    icon?: string
}

interface ValidationError {
    group_id: number
    message: string
}

const props = defineProps({
    group: {
        type: Object as PropType<ServiceGroup>,
        required: true
    },
    selectedServices: {
        type: Array as PropType<number[]>,
        default: () => []
    },
    existingServices: {
        type: Array as PropType<number[]>,
        default: () => []
    },
    // Booked services that can NOT be removed (e.g. completed for a non-staff
    // user) — an exclusive pick that would need to replace one of these is
    // blocked, and they stay inert (not tappable for removal). DEV-115.
    lockedExistingServices: {
        type: Array as PropType<number[]>,
        default: () => []
    },
    // Booked services currently queued for removal (tapped in-place). Rendered in
    // a red "wird entfernt" state. DEV-119.
    removingServices: {
        type: Array as PropType<number[]>,
        default: () => []
    },
    // Booked services that may be removed in place (canRemoveService — permissive:
    // completed/started removable by dispatcher/checker). Distinct from
    // lockedExistingServices, which is the stricter reason-less *swap* set. DEV-119.
    removableExistingServices: {
        type: Array as PropType<number[]>,
        default: () => []
    },
    // Services blocked by a pairwise exclusion rule against the current active
    // set (selected + booked): service id → rule message. Blocked tiles render
    // disabled; tapping one surfaces the message via exclusionBlocked. DEV-219.
    exclusionConflicts: {
        type: Object as PropType<Record<number, string>>,
        default: () => ({})
    },
    validationErrors: {
        type: Array as PropType<ValidationError[]>,
        default: () => []
    },
    // A service that REPLACES the turnaround is selected, so the group
    // obligation is lifted (DEV-240 / TRK-15). Decided by the parent, which
    // knows the role — the exemption is limited to dispatcher/schichtleiter/admin.
    turnaroundReplaced: {
        type: Boolean,
        default: false
    },
    defaultExpanded: {
        type: Boolean,
        default: true
    }
})

const emit = defineEmits<{
    (e: 'update:selectedServices', value: number[]): void
    // Exclusive-choice swap: ask the parent to remove the already-booked
    // sibling(s) so the newly picked option can replace them (DEV-115).
    (e: 'requestRemoveExisting', serviceIds: number[]): void
    // Exclusive pick refused because the booked sibling can't be replaced.
    (e: 'swapBlocked', serviceId: number): void
    // A booked service tile was tapped to toggle it for removal (DEV-119).
    (e: 'toggleRemoveExisting', serviceId: number): void
    // A tile blocked by an exclusion rule was tapped; the pick was refused. DEV-219.
    (e: 'exclusionBlocked', serviceId: number, message: string): void
}>()

const expanded = ref(props.defaultExpanded)

// Computed properties
const groupIcon = computed(() => {
    return props.group.icon || getIconForGroup(props.group.name)
})

const isMandatory = computed(() => isMandatoryGroup(props.group))

// Combined services: existing + newly selected (for validation)
const allSelectedServices = computed(() => {
    const combined = [...props.existingServices, ...props.selectedServices]
    return [...new Set(combined)]
})

const selectedInGroup = computed(() => {
    return props.group.services.filter(s => allSelectedServices.value.includes(s.id))
})

const selectedCount = computed(() => selectedInGroup.value.length)

// Delegates: one client-side implementation of the rule, in useServiceGroups.
const isValid = computed(() =>
    validateGroupLocally(props.group, allSelectedServices.value, props.turnaroundReplaced)
)

const requirementText = computed(() => {
    // Only a group that demands something can stop demanding it, and only one
    // the exemption actually satisfies — with two members of an exclusive group
    // the save is still refused, so "Entfällt" there would contradict the gate.
    if (props.turnaroundReplaced && isMandatory.value && isValid.value) {
        return 'Entfällt — Abholung durch Fremdfirma'
    }

    switch (props.group.type) {
        case 'mandatory_choice':
            return 'Mindestens 1 auswählen'
        case 'exclusive_choice':
            return 'Genau 1 auswählen'
        case 'multi_required':
            return `Mindestens ${requiredMinimum(props.group)} auswählen`
        default:
            return 'Optional'
    }
})

const hasValidationError = computed(() => {
    return props.validationErrors.some(error => error.group_id === props.group.id)
})

const validationMessage = computed(() => {
    if (hasValidationError.value) {
        const error = props.validationErrors.find(e => e.group_id === props.group.id)
        return error?.message
    }
    return null
})

const cardClasses = computed(() => ({
    'border-red-300 bg-red-50/30': hasValidationError.value,
    'border-green-300': isValid.value && isMandatory.value && !hasValidationError.value,
    'border-gray-200': !hasValidationError.value && !(isValid.value && isMandatory.value)
}))

const headerClasses = computed(() => ({
    'bg-red-50': hasValidationError.value,
    'bg-green-50': isValid.value && isMandatory.value && !hasValidationError.value,
    'bg-gray-50': !hasValidationError.value && !(isValid.value && isMandatory.value)
}))

// Methods
const toggleExpanded = () => {
    expanded.value = !expanded.value
}

const isServiceSelected = (serviceId: number): boolean => {
    // Include both existing and newly selected services
    return allSelectedServices.value.includes(serviceId)
}

const isServiceBooked = (serviceId: number): boolean => {
    // Originally assigned to the checkin. existingServices is the *effective* set
    // (booked minus queued-for-removal, for validation), so the booked notion for
    // display/tap is the union with removingServices. DEV-119.
    return props.existingServices.includes(serviceId) || props.removingServices.includes(serviceId)
}

const isServiceRemoving = (serviceId: number): boolean => {
    return props.removingServices.includes(serviceId)
}

// A booked service that may be removed in place (per canRemoveService, passed in
// as removableExistingServices). Non-removable booked services stay inert. DEV-119.
const isServiceRemovable = (serviceId: number): boolean => {
    return props.removableExistingServices.includes(serviceId)
}

// Blocked by an exclusion rule against the active set (DEV-219). Active tiles
// (selected/booked) are never marked — deselecting the trigger frees them again.
const isServiceExcluded = (serviceId: number): boolean => {
    return !isServiceSelected(serviceId) && props.exclusionConflicts[serviceId] !== undefined
}

const isServiceDisabled = (serviceId: number): boolean => {
    // Inputs are only truly disabled for locked booked services. Removable booked
    // services are interactive (tap = toggle removal), handled in onTileClick.
    return (isServiceBooked(serviceId) && !isServiceRemovable(serviceId)) || isServiceExcluded(serviceId)
}

// Full class string for a service tile, covering: queued-for-removal (red),
// booked+removable (green, tappable), booked+locked (green, inert), newly
// selected (brand red), and unselected (gray). DEV-119.
const serviceCardClass = (serviceId: number): string => {
    if (isServiceRemoving(serviceId)) {
        return 'border-red-400 bg-red-50 cursor-pointer'
    }
    if (isServiceBooked(serviceId)) {
        return isServiceRemovable(serviceId)
            ? 'border-green-400 bg-green-50 cursor-pointer'
            : 'border-green-400 bg-green-50 cursor-not-allowed opacity-75'
    }
    if (isServiceSelected(serviceId)) {
        return 'border-[#E9041D] bg-red-50 cursor-pointer'
    }
    if (isServiceExcluded(serviceId)) {
        // Excluded by a rule: greyed like a locked booked tile. DEV-219.
        return 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
    }
    return 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
}

const serviceIndicatorClass = (serviceId: number): string => {
    if (isServiceRemoving(serviceId)) return 'border-red-500 bg-red-500'
    if (isServiceSelected(serviceId)) {
        return isServiceBooked(serviceId) ? 'border-green-500 bg-green-500' : 'border-[#E9041D] bg-[#E9041D]'
    }
    return 'border-gray-300'
}

// Tap on a tile. Booked services route to removal toggling (and never to the
// add/swap path); non-booked tiles fall through to the native input change.
const onTileClick = (event: Event, serviceId: number) => {
    if (isServiceBooked(serviceId)) {
        event.preventDefault()
        if (isServiceRemovable(serviceId)) {
            emit('toggleRemoveExisting', serviceId)
        }
        return
    }
    // Excluded tile: refuse the pick and surface why. DEV-219.
    if (isServiceExcluded(serviceId)) {
        event.preventDefault()
        emit('exclusionBlocked', serviceId, props.exclusionConflicts[serviceId])
    }
}

const onInputChange = (event: Event, serviceId: number) => {
    const target = event.target as HTMLInputElement
    handleServiceSelection(serviceId, target.checked)
}

const handleServiceSelection = (serviceId: number, isChecked: boolean) => {
    // Booked services are removal toggles, not add/swap. Routing them here too
    // keeps keyboard activation (space on the input) consistent with onTileClick
    // (pointer): removable → toggle removal, locked → inert. DEV-119.
    if (isServiceBooked(serviceId)) {
        if (isServiceRemovable(serviceId)) {
            emit('toggleRemoveExisting', serviceId)
        }
        return
    }

    // Keyboard-path guard for excluded tiles (mirrors onTileClick). DEV-219.
    if (isChecked && isServiceExcluded(serviceId)) {
        emit('exclusionBlocked', serviceId, props.exclusionConflicts[serviceId])
        return
    }

    let newSelection = [...props.selectedServices]

    if (props.group.type === 'exclusive_choice') {
        // Remove all other newly-selected services from this group
        props.group.services.forEach(s => {
            if (!isServiceDisabled(s.id)) {
                const idx = newSelection.indexOf(s.id)
                if (idx > -1) newSelection.splice(idx, 1)
            }
        })
        // Exclusive = exactly one. If a sibling is already booked, ask the parent
        // to swap it out so the new pick can replace it instead of stacking on top.
        if (isChecked) {
            const bookedSiblings = props.group.services
                .filter(s => s.id !== serviceId && props.existingServices.includes(s.id))
                .map(s => s.id)
            const lockedSibling = bookedSiblings.find(id => props.lockedExistingServices.includes(id))
            if (lockedSibling !== undefined) {
                // The booked option can't be removed (e.g. completed) → can't swap.
                emit('swapBlocked', lockedSibling)
                return
            }
            if (bookedSiblings.length > 0) {
                emit('requestRemoveExisting', bookedSiblings)
            }
            newSelection.push(serviceId)
        }
    } else {
        // Regular checkbox behavior
        if (isChecked) {
            if (!newSelection.includes(serviceId)) {
                newSelection.push(serviceId)
            }
        } else {
            const idx = newSelection.indexOf(serviceId)
            if (idx > -1) newSelection.splice(idx, 1)
        }
    }

    emit('update:selectedServices', newSelection)
}

// Auto-expand groups with errors
watch(() => hasValidationError.value, (hasError) => {
    if (hasError) {
        expanded.value = true
    }
})
</script>

<style scoped>
.service-group-card {
    user-select: none;
}

.service-card:active {
    transform: scale(0.98);
}
</style>
